<template>
  <section
    class="min-h-screen bg-surface-base text-fg-primary text-fg-g-primary print:!bg-transparent print:!text-black"
  >
    <Message
      v-show="hasVenueAppVersionChanged"
      id="venue-version-alert"
      class="fixed z-50 w-auto bottom-10 mx-8"
      severity="secondary"
      pt:content:class="!h-auto"
      closable
    >
      There's a new version of TurnTable available.
      <a id="refresh-button" class="text-fg-link-primary cursor-pointer" @click="refresh">
        Refresh to get the latest.
      </a>
    </Message>
    <HeaderComponent class="print:hidden" v-show="$route.meta.header !== false && !isFullscreen" />
    <div class="w-full" :class="{ 'px-4 lg:px-16 py-4': !isSettingsPage }">
      <VenueStripeAlert v-show="!isFullscreen" />
      <router-view />
    </div>

    <DevelopmentIndicator />
    <Toast />
  </section>
</template>

<script lang="ts">
import { venueRoutes } from './router/venue-routes'
import { useIntercom } from '@homebaseai/vue3-intercom'
import { breakpointsTailwind, useBreakpoints, useEventBus, useWebSocket } from '@vueuse/core'
import { useFullscreen } from '@vueuse/core'
import { mapStores, storeToRefs } from 'pinia'
import Message from 'primevue/message'
import Toast from 'primevue/toast'
import { defineComponent } from 'vue'

import { guestKey, onVenueMessage } from '@/api/events'
import DevelopmentIndicator from '@/common/components/atoms/DevelopmentIndicator.vue'
import { useFeatureFlags } from '@/common/composables/useFeatureFlags'
import { usePerformanceReservationStore } from '@/modules/performance/stores/PerformanceReservationStore'
import VenueStripeAlert from '@/modules/venueUi/components/molecules/VenueStripeAlert.vue'
import HeaderComponent from '@/modules/venueUi/components/organisms/HeaderComponent.vue'
import { useCheckoutStore } from '@/stores/checkout'
import { useSettingStore } from '@/stores/setting'
import { useUserStore } from '@/stores/user'
import loc from '@/utils/loc'

export default defineComponent({
  name: 'AppVenue',
  components: {
    VenueStripeAlert,
    Message,
    HeaderComponent,
    DevelopmentIndicator,
    Toast,
  },

  computed: {
    ...mapStores(useSettingStore, useCheckoutStore),
    hasVenueAppVersionChanged(): boolean {
      return this.settingStore.hasVenueAppVersionChanged
    },
    isLargeScreen(): boolean {
      return useBreakpoints(breakpointsTailwind).lg.value
    },
    isOnboardingFlow(): boolean {
      return !!this.$route.meta?.onboarding
    },
    isSettingsPage(): boolean {
      return this.$route.path?.includes('venue/settings')
    },
  },
  setup() {
    //@ts-ignore
    venueRoutes[0].component() // force load the venue chunk
    useWebSocket(`wss://${window.location.host}/ws/`, {
      autoReconnect: {
        retries: 5,
        delay: 1000,
      },
      onMessage: onVenueMessage,
    })
    useEventBus(guestKey).on(usePerformanceReservationStore().onGuestChange)

    const { isIntercomChatEnabled } = useFeatureFlags()
    const userStore = useUserStore()
    const { me } = storeToRefs(userStore)
    if (isIntercomChatEnabled.value && me.value.id) {
      const intercom = useIntercom()
      const bootData: any = {
        app_id: 'pyp7ps5m',
        user_id: String(me.value.id),
        name: me.value.name,
        email: me.value.email,
        created_at: me.value.last_login,
        venue: useSettingStore().venue_slug,
      }
      
      // Add JWT token for identity verification if available
      if ((me.value as any).intercom_jwt) {
        bootData.user_hash = (me.value as any).intercom_jwt
      }
      
      intercom.boot(bootData)
    }
    const { isFullscreen } = useFullscreen()
    return { isFullscreen }
  },
  methods: {
    refresh() {
      loc.reload()
    },
    updateTheme(): void {
      document.body.setAttribute('class', 'venue-ui')
    },
  },
  mounted() {
    if (document) {
      const hsScript = document.createElement('script')
      hsScript.src = '//js.hs-scripts.com/22692115.js'
      document.head.appendChild(hsScript)
    }
    this.updateTheme()
  },
  watch: {
    '$route.path'(value: string) {
      if (value.startsWith('/venue')) {
        this.checkoutStore.performance = null
      }
      this.updateTheme()
    },
  },
})
</script>
